import { getMockOverrideSpec } from "@app/apollo/mocks/aiMock/mockOverrideStorage";
import { hasPartnerId } from "@pages/partner/helpers/hasPartnerId";
import { type AuthOptions, createAuthClient, generateOnBehalfOfScope } from "@storebrand-digital/auth";
import { getCMIDfromQuery } from "@utils/helpers/getCMIDfromQuery";
import { getPartnerIdFromUrl } from "@utils/helpers/getPartnerIdFromUrl";
import { ninForAge } from "@utils/helpers/ninForAge";
import { PartnerId } from "@utils/types";

import { isOffline, isProduction } from "./env";

const getLogoutRedirectUri = (): string =>
  !isProduction() ? "https://www-t.storebrand.no/logg-ut/" : "https://www.storebrand.no/logg-ut/";

const createClientIdFromPartnerConfig = (partnerId: PartnerId): string => `storebrand.savings.savings-web.${partnerId}`;
const cmid = getCMIDfromQuery();
const { env } = window;
const isAdvisor = !!cmid;
const scope = isAdvisor ? generateOnBehalfOfScope(cmid) : env.KEYCLOAK_SCOPE;
const clientId = hasPartnerId() ? createClientIdFromPartnerConfig(getPartnerIdFromUrl()) : env.KEYCLOAK_CLIENT_ID;

const options: AuthOptions = {
  config: {
    realm: isAdvisor ? env.ADVISOR_KEYCLOAK_REALM : env.KEYCLOAK_REALM,
    clientId: isAdvisor ? env.ADVISOR_KEYCLOAK_CLIENT_ID : clientId,
    url: isAdvisor ? env.ADVISOR_KEYCLOAK_URL : env.KEYCLOAK_URL
  },
  init: { scope },
  login: { scope },
  logout: { redirectUri: getLogoutRedirectUri() }
};

// Offline/dev only. Age-gated dev/mock UI reads the user's age from the fødselsnummer via `getUserAge`
// (e.g. the investment-allocation downscaling card needs age > 57). When an AI-mock persona sets
// `persona.age`, synthesize a matching fake fødselsnummer; otherwise fall back to a stable default
// (01.01.1965 → age > 57). `getMockOverrideSpec` is `isDevPanelEnabled()`-gated and this client is only
// constructed when `isOffline()`, so there is zero production footprint. Not a real person's number.
const DEFAULT_MOCK_NIN = "01016512345";
const getMockUserNin = (): string => {
  const age = getMockOverrideSpec()?.persona?.age;
  return typeof age === "number" ? ninForAge(age) : DEFAULT_MOCK_NIN;
};

class AuthenticatedKeycloakClient {
  public init(): Promise<boolean> {
    return Promise.resolve(true);
  }

  public authenticated = true;

  public login(): Promise<void> {
    return Promise.resolve();
  }

  public logout(): Promise<void> {
    return Promise.resolve();
  }

  public getToken(): Promise<string> {
    return Promise.resolve("");
  }

  idTokenParsed = {
    cm_id: "cm_id",
    // Offline/dev only — a fake fødselsnummer derived from the persona's `age` (or the default when
    // unset). See `getMockUserNin` above. Previously a fixed `"no_nin"` parsed to a null age, hiding
    // age-gated UI such as the downscaling (nedtrapping) card.
    no_nin: getMockUserNin(),
    amr: "amr"
  };
}

const auth = !isOffline() ? createAuthClient(options) : new AuthenticatedKeycloakClient();
export default auth;
